/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Collection } from '@quintype/components';
import { SvgHandler } from '../../svg-handler/svg-handler';
import faceFrawnIcon from '../../../../assets/icons/face-frown-icon.svg';
import './purchased-stories.m.css';
import { collectionWithIntroductionCard, getDynamicAssets } from '../../../utils';
import get from 'lodash.get';
import { getCollectionTemplate } from '../../get-collection-template';
import { PurchasedStoriesComponent } from './PurchasedStoriesComponent';
import { Button } from '../login/atoms/button';
import LoaderOverlay from '../../molecules/loader-overlay';

const EmptyPurchasedStories = ({ localizedNoStories, config, isDarkModeEnabled }) => {
  const { pageTitle = 'Purchased Stories' } = get(
    config,
    ['pagebuilder-config', 'purchasedStoryPage', 'rows', 0, 'config'],
    {}
  );
  const { emptyStoriesLabel, redirectHomePage } = get(
    config,
    ['pagebuilder-config', 'purchasedStoryPage', 'pageSetting'],
    {}
  );
  return (
    <div styleName="empty-stories-wrapper">
      <h1>{pageTitle}</h1>
      <div style={isDarkModeEnabled ? { color: '#ffffff' } : {}}>
        <SvgHandler
          width="50"
          height="50"
          clazzName="face-frawn-icon"
          xlinkHref={faceFrawnIcon}
          invertColor={isDarkModeEnabled || false}
        />
        <p>{emptyStoriesLabel || localizedNoStories || 'You have not bought any stories yet.'}</p>
      </div>
      <Button
        text={redirectHomePage || 'Go to Home'}
        clickHandler={() => (window.location.href = '/')}
        enableEnterClick={true}
      />
    </div>
  );
};

EmptyPurchasedStories.propTypes = {
  localizedNoStories: PropTypes.string,
  config: PropTypes.object,
  isDarkModeEnabled: PropTypes.bool
};

export const PurchasedStoriesBlock = ({
  purchasedStories = {},
  collection = {},
  config,
  isDarkModeEnabled,
  getSubscriptionForUser,
  isAccessTypeLoaded,
  enableAccesstype,
  memberSubscriptions
}) => {
  const [assetStories, setAssetStories] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const { noStories: localizedNoStories = '' } = purchasedStories;

  const getUserSubscriptions = async () => {
    const { subscriptions: memberSubscriptions } = (await getSubscriptionForUser()) || [];
    const dynamicAssets = await getDynamicAssets(memberSubscriptions);
    if (dynamicAssets.length > 0) {
      setAssetStories(dynamicAssets);
      setIsDataLoading(false);
    } else {
      setIsDataLoading(false);
    }
  };

  useEffect(() => {
    getUserSubscriptions();
  }, [isAccessTypeLoaded, enableAccesstype, memberSubscriptions]);

  if (isDataLoading) return <LoaderOverlay />;

  if (!assetStories.length && !isDataLoading)
    return (
      <EmptyPurchasedStories
        localizedNoStories={localizedNoStories}
        config={config}
        isDarkModeEnabled={isDarkModeEnabled}
      />
    );
  const isPbEnabled = get(config, ['pagebuilder-config', 'purchasedStoryPage'], true);
  if (isPbEnabled) {
    const purchasedStoryPageRows = get(config, ['pagebuilder-config', 'purchasedStoryPage', 'rows'], []);
    const purchasedStoryConfig = purchasedStoryPageRows.find((row) => row.type === 'collections');
    const { config: rowConfig } = purchasedStoryConfig || {};
    const { initialLoadCount = '' } = rowConfig;
    const FALLBACK_PAGE_TITLE = 'Purchased Stories';
    const FALLBACK_PAGE_DESCRIPTION =
      "View all the articles you've purchased on this page. Explore your collections and enjoy reading!";
    // Setting up default keys for the introduction card config
    config['pagebuilder-config'].purchasedStoryPage.rows[0].config = {
      ...config?.['pagebuilder-config']?.purchasedStoryPage?.rows?.[0]?.config,
      pageTitle: config?.['pagebuilder-config']?.purchasedStoryPage?.rows?.[0]?.config.pageTitle || FALLBACK_PAGE_TITLE,
      pageDescription:
        config?.['pagebuilder-config']?.purchasedStoryPage?.rows?.[0]?.config.pageDescription ||
        FALLBACK_PAGE_DESCRIPTION
    };
    // Setting up default keys for the collection card config
    if (initialLoadCount === '') {
      config['pagebuilder-config'].purchasedStoryPage.rows[1].config = {
        ...config?.['pagebuilder-config']?.purchasedStoryPage?.rows?.[1]?.config,
        initialLoadCount: 4,
        subsequentLoadCount: 4
      };
    }
    const collection = collectionWithIntroductionCard(config, assetStories, 'purchasedStoryPage', '');
    return <Collection config={config} collection={collection} collectionTemplates={getCollectionTemplate} />;
  }
  return <PurchasedStoriesComponent collection={collection} config={config} />;
};

PurchasedStoriesBlock.propTypes = {
  purchasedStories: PropTypes.shape({
    noStories: PropTypes.string
  }),
  collection: PropTypes.object,
  config: PropTypes.object,
  isDarkModeEnabled: PropTypes.bool,
  getSubscriptionForUser: PropTypes.func,
  isAccessTypeLoaded: PropTypes.any,
  enableAccesstype: PropTypes.any,
  memberSubscriptions: PropTypes.any
};
